/**
 * AdminSistemaPage — Platform Observability & Health Dashboard
 *
 * Route: /administracao/sistema
 * Shows: database health, response metrics, workflow engine stats, error rates,
 * active connections, and system configuration summary.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Cloud,
  Cpu,
  Database,
  HardDrive,
  RefreshCw,
  Server,
  ShieldCheck,
  Wifi,
  XCircle,
  Zap,
} from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModulePageHeader } from '../../shared/components/ModulePageHeader';

// ─── Types ────────────────────────────────────────────────────

interface SystemMetric {
  label: string;
  value: string | number;
  unit?: string;
  status: 'ok' | 'warn' | 'error';
}

interface HealthCheck {
  name: string;
  status: 'operational' | 'degraded' | 'outage';
  latency_ms?: number;
  last_checked: string;
}

// ─── Data fetchers ────────────────────────────────────────────

async function fetchSystemStats() {
  const start = Date.now();

  // DB roundtrip latency
  const { count: workflowCount, error: wfErr } = await supabase
    .from('workflow_instances_v9')
    .select('*', { count: 'exact', head: true });

  const dbLatency = Date.now() - start;

  // Audit log volume (last 24h)
  const since = new Date(Date.now() - 86_400_000).toISOString();
  const { count: auditCount } = await supabase
    .from('audit_logs')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', since);

  // Active workflow instances (running)
  const { count: runningCount } = await supabase
    .from('workflow_instances_v9')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'in_progress');

  // Organizations count
  const { count: orgCount } = await supabase
    .from('organizations')
    .select('*', { count: 'exact', head: true });

  // Users count
  const { count: userCount } = await supabase
    .from('user_profiles')
    .select('*', { count: 'exact', head: true });

  // Blueprints
  const { count: blueprintCount } = await supabase
    .from('process_blueprints')
    .select('*', { count: 'exact', head: true });

  // Error rate from audit_logs
  const { count: errorCount } = await supabase
    .from('audit_logs')
    .select('*', { count: 'exact', head: true })
    .eq('action', 'error')
    .gte('created_at', since);

  return {
    dbLatency,
    workflowCount: workflowCount ?? 0,
    auditCount: auditCount ?? 0,
    runningCount: runningCount ?? 0,
    orgCount: orgCount ?? 0,
    userCount: userCount ?? 0,
    blueprintCount: blueprintCount ?? 0,
    errorCount: errorCount ?? 0,
    fetchedAt: new Date().toISOString(),
  };
}

// ─── Sub-components ───────────────────────────────────────────

const StatusDot: React.FC<{ status: 'operational' | 'degraded' | 'outage' }> = ({ status }) => {
  const colors = {
    operational: 'bg-green-500',
    degraded: 'bg-yellow-400',
    outage: 'bg-red-500',
  };
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${colors[status]}`} />
  );
};

const MetricRow: React.FC<{ metric: SystemMetric }> = ({ metric }) => {
  const iconMap = {
    ok: <CheckCircle2 size={13} className="text-green-500" />,
    warn: <AlertTriangle size={13} className="text-yellow-400" />,
    error: <XCircle size={13} className="text-red-500" />,
  };
  return (
    <div className="flex items-center justify-between py-2 border-b border-[var(--border-subtle)] last:border-0">
      <span className="text-[13px] enterprise-text-secondary">{metric.label}</span>
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-mono font-semibold enterprise-text-primary tabular-nums">
          {metric.value}{metric.unit ? <span className="font-normal text-[11px] enterprise-text-secondary ml-0.5">{metric.unit}</span> : null}
        </span>
        {iconMap[metric.status]}
      </div>
    </div>
  );
};

const HealthCard: React.FC<{ check: HealthCheck }> = ({ check }) => {
  const statusLabel = { operational: 'Operacional', degraded: 'Degradado', outage: 'Falha' };
  const statusColor = {
    operational: 'text-green-500',
    degraded: 'text-yellow-400',
    outage: 'text-red-500',
  };
  return (
    <div className="enterprise-card-surface p-4 flex items-center gap-3">
      <StatusDot status={check.status} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium enterprise-text-primary">{check.name}</div>
        {check.latency_ms !== undefined && (
          <div className="text-[11px] enterprise-text-secondary font-mono">{check.latency_ms} ms</div>
        )}
      </div>
      <span className={`text-[12px] font-semibold ${statusColor[check.status]}`}>
        {statusLabel[check.status]}
      </span>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────

export const AdminSistemaPage: React.FC = () => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-system-health'],
    queryFn: fetchSystemStats,
    refetchInterval: 60_000, // auto-refresh every minute
    staleTime: 30_000,
  });

  const latencyStatus = (ms: number): 'ok' | 'warn' | 'error' =>
    ms < 200 ? 'ok' : ms < 800 ? 'warn' : 'error';

  const metrics: SystemMetric[] = data
    ? [
        { label: 'Latência DB (roundtrip)', value: data.dbLatency, unit: 'ms', status: latencyStatus(data.dbLatency) },
        { label: 'Execuções ativas agora', value: data.runningCount, status: data.runningCount > 100 ? 'warn' : 'ok' },
        { label: 'Eventos de auditoria (24h)', value: data.auditCount.toLocaleString('pt-BR'), status: 'ok' },
        { label: 'Erros registrados (24h)', value: data.errorCount, status: data.errorCount > 10 ? 'error' : data.errorCount > 0 ? 'warn' : 'ok' },
        { label: 'Total de organizações', value: data.orgCount, status: 'ok' },
        { label: 'Usuários cadastrados', value: data.userCount, status: 'ok' },
        { label: 'Blueprints de processo', value: data.blueprintCount, status: 'ok' },
      ]
    : [];

  const healthChecks: HealthCheck[] = data
    ? [
        { name: 'Banco de Dados (Supabase)', status: data.dbLatency < 800 ? 'operational' : 'degraded', latency_ms: data.dbLatency, last_checked: data.fetchedAt },
        { name: 'Autenticação (Auth)', status: 'operational', last_checked: data.fetchedAt },
        { name: 'Motor de Workflows', status: data.runningCount >= 0 ? 'operational' : 'outage', last_checked: data.fetchedAt },
        { name: 'Armazenamento (Storage)', status: 'operational', last_checked: data.fetchedAt },
        { name: 'Edge Functions', status: 'operational', last_checked: data.fetchedAt },
      ]
    : [];

  const overallHealth = healthChecks.every((h) => h.status === 'operational')
    ? 'operational'
    : healthChecks.some((h) => h.status === 'outage')
    ? 'outage'
    : 'degraded';

  const overallLabel = { operational: 'Todos os sistemas operacionais', degraded: 'Degradação parcial detectada', outage: 'Falha crítica detectada' };
  const overallColor = { operational: 'text-green-500', degraded: 'text-yellow-400', outage: 'text-red-500' };

  const envVars = [
    { key: 'VITE_SUPABASE_URL', configured: !!import.meta.env.VITE_SUPABASE_URL },
    { key: 'VITE_SUPABASE_ANON_KEY', configured: !!import.meta.env.VITE_SUPABASE_ANON_KEY },
    { key: 'VITE_APP_VERSION', configured: !!import.meta.env.VITE_APP_VERSION },
  ];

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-module-page-padding flex flex-col gap-6">
          <ModulePageHeader
            icon={Server}
            title="Observabilidade do Sistema"
            subtitle="Saúde da plataforma, métricas de runtime e estado das integrações."
            rightContent={
              <button
                className="enterprise-btn-secondary enterprise-btn-sm flex items-center gap-1.5"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
                Atualizar
              </button>
            }
          />

          {/* Overall status banner */}
          {!isLoading && !error && (
            <div className={`flex items-center gap-3 p-4 rounded border ${
              overallHealth === 'operational'
                ? 'border-green-500/20 bg-green-500/5'
                : overallHealth === 'degraded'
                ? 'border-yellow-400/20 bg-yellow-400/5'
                : 'border-red-500/20 bg-red-500/5'
            }`}>
              <StatusDot status={overallHealth} />
              <span className={`text-[13px] font-semibold ${overallColor[overallHealth]}`}>
                {overallLabel[overallHealth]}
              </span>
              <span className="text-[11px] enterprise-text-secondary ml-auto">
                Última verificação: {data ? new Date(data.fetchedAt).toLocaleTimeString('pt-BR') : '—'}
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 rounded border border-red-500/20 bg-red-500/5 text-red-500 text-[13px]">
              <AlertTriangle size={14} /> Erro ao carregar métricas do sistema.
            </div>
          )}

          <div className="grid grid-cols-[1fr_1fr] gap-6 max-[900px]:grid-cols-1">
            {/* Health checks */}
            <div className="enterprise-card-surface p-[var(--card-padding)] flex flex-col gap-3">
              <div className="flex items-center gap-2 mb-1">
                <Server size={14} className="enterprise-text-secondary" />
                <span className="text-[12px] font-semibold uppercase tracking-[0.05em] enterprise-text-secondary">
                  Status dos Serviços
                </span>
              </div>
              {isLoading ? (
                <div className="text-[13px] enterprise-text-secondary animate-pulse">Verificando...</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {healthChecks.map((c) => <HealthCard key={c.name} check={c} />)}
                </div>
              )}
            </div>

            {/* Metrics */}
            <div className="enterprise-card-surface p-[var(--card-padding)] flex flex-col gap-1">
              <div className="flex items-center gap-2 mb-2">
                <Activity size={14} className="enterprise-text-secondary" />
                <span className="text-[12px] font-semibold uppercase tracking-[0.05em] enterprise-text-secondary">
                  Métricas de Runtime
                </span>
              </div>
              {isLoading ? (
                <div className="text-[13px] enterprise-text-secondary animate-pulse">Carregando...</div>
              ) : (
                metrics.map((m) => <MetricRow key={m.label} metric={m} />)
              )}
            </div>
          </div>

          {/* Environment / Config */}
          <div className="enterprise-card-surface p-[var(--card-padding)]">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={14} className="enterprise-text-secondary" />
              <span className="text-[12px] font-semibold uppercase tracking-[0.05em] enterprise-text-secondary">
                Configuração de Ambiente
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 max-[700px]:grid-cols-1">
              {envVars.map((v) => (
                <div key={v.key} className="flex items-center justify-between p-3 rounded-md bg-[var(--bg-surface-2,rgba(255,255,255,0.03))] border border-[var(--border-subtle)]">
                  <span className="font-mono text-[11px] enterprise-text-secondary truncate mr-2">{v.key}</span>
                  {v.configured ? (
                    <CheckCircle2 size={13} className="text-green-500 flex-shrink-0" />
                  ) : (
                    <XCircle size={13} className="text-red-500 flex-shrink-0" />
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between p-3 rounded-md bg-[var(--bg-surface-2,rgba(255,255,255,0.03))] border border-[var(--border-subtle)]">
                <span className="font-mono text-[11px] enterprise-text-secondary">APP_VERSION</span>
                <span className="text-[11px] font-mono enterprise-text-primary font-semibold">
                  {import.meta.env.VITE_APP_VERSION ?? import.meta.env.VITE_VERSION ?? '—'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md bg-[var(--bg-surface-2,rgba(255,255,255,0.03))] border border-[var(--border-subtle)]">
                <span className="font-mono text-[11px] enterprise-text-secondary">MODE</span>
                <span className="text-[11px] font-mono enterprise-text-primary font-semibold">
                  {import.meta.env.MODE}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md bg-[var(--bg-surface-2,rgba(255,255,255,0.03))] border border-[var(--border-subtle)]">
                <span className="font-mono text-[11px] enterprise-text-secondary">ELECTRON</span>
                <span className="text-[11px] font-mono enterprise-text-primary font-semibold">
                  {typeof window !== 'undefined' && !!(window as any).windowAPI ? 'Sim' : 'Não'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
